import React from 'react';
import { styled } from '@mui/material/styles';
import { useAppDispatch } from 'store/hooks';
import { Box, IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import AddShoppingCartOutlined from '@mui/icons-material/AddShoppingCartOutlined';
import clsx from 'clsx';
import { useCalories } from 'src/hooks/useCalories';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { IOrderReadResourceV18 } from 'components/order/model/Order';
import { getDay } from 'components/timeslots/helpers';
import { getCurrencyString, showCalories } from 'lib/helpers';
import { addBasketItems } from 'store/basket/basketSlice';
import { isDefined } from 'lib/typeInference';
import { v4 as uuid } from 'uuid';
import { EGAEventName, isSweetwaters, useGAHelpers } from 'lib/useGAHelpers';
import { useParams } from 'react-router';
import { LocationRouteParams } from 'pages/routes';

const PREFIX = 'RecentOrder';

const classes = {
    textOpacity: `${PREFIX}textOpacity`,
    icon: `${PREFIX}icon`,
    productNames: `${PREFIX}productNames`,
    iconButton: `${PREFIX}iconButton`
};

const StyledBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    [`& .${classes.textOpacity}`]: {
        opacity: '0.7'
    },

    [`& .${classes.icon}`]: {
        color: theme.palette.hint
    },

    [`& .${classes.productNames}`]: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        lineClamp: 2,
        display: 'box',
        '-webkit-box-orient': 'vertical',
        height: '48px'
    },

    [`& .${classes.iconButton}`]: {
        padding: theme.spacing(1, 1.5)
    }
}));

interface IRecentOrderProps {
    order: IOrderReadResourceV18;
    onOrderClick: (order: IOrderReadResourceV18) => void;
    orderingEnabled?: boolean;
}

export const RecentOrder: React.FC<React.PropsWithChildren<IRecentOrderProps>> = ({
    onOrderClick,
    order,
    orderingEnabled
}) => {
    const { getOrderItemsCalories, getCaloriesString } = useCalories();
    const { total, items, createdAt, currencyCode } = order;

    const dispatch = useAppDispatch();
    const { merchantId } = useParams<LocationRouteParams>();
    const { logUserEvent } = useGAHelpers();

    const date = React.useMemo(() => {
        if (!!createdAt) {
            return getDay(createdAt);
        }
        return null;
    }, [createdAt]);
    const handleAddToBasketClick = React.useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (isSweetwaters(merchantId)) {
                logUserEvent(EGAEventName.QuickAddRecentOrdersSweetwaters);
            }
            const mainItems = items.filter(item => !item.parentReferenceId);
            const itemsSortedForBasket = mainItems.reduce<IItemReadResourceV10[]>(
                (acc, item) => [
                    ...acc,
                    item,
                    ...items.filter(
                        innerItem =>
                            !!innerItem.parentReferenceId && innerItem.parentReferenceId === item.referenceId
                    )
                ],
                []
            );
            const referenceIds: Record<string, string> = {};
            const itemsToPush = itemsSortedForBasket.map(item => {
                const { productId, categoryId, quantity, modifiers, referenceId, parentReferenceId, cost } =
                    item;
                let newReferenceId = referenceId ?? uuid();
                let newParentReferenceId = parentReferenceId;

                if (isDefined(newReferenceId)) {
                    if (!isDefined(referenceIds[newReferenceId])) {
                        referenceIds[newReferenceId] = uuid();
                    }
                    newReferenceId = referenceIds[newReferenceId];
                }
                if (isDefined(newParentReferenceId)) {
                    if (!isDefined(referenceIds[newParentReferenceId])) {
                        referenceIds[newParentReferenceId] = uuid();
                    }
                    newParentReferenceId = referenceIds[newParentReferenceId];
                }

                return {
                    productId,
                    categoryId,
                    quantity,
                    modifiers,
                    referenceId: newReferenceId,
                    parentReferenceId: newParentReferenceId,
                    price: cost
                };
            });
            dispatch(addBasketItems(itemsToPush));
        },
        [dispatch, items, logUserEvent, merchantId]
    );
    const handleOrderClick = React.useCallback(() => {
        onOrderClick(order);
    }, [onOrderClick, order]);
    const productsString = React.useMemo(() => {
        const products = items.filter(item => !item.parentReferenceId);

        return products.map(product => `${product.quantity}x ${product.productName}`).join(', ');
    }, [items]);
    const totalOrderCalories = React.useMemo(
        () => getOrderItemsCalories(order.items),
        [getOrderItemsCalories, order.items]
    );

    const priceString = React.useMemo(() => {
        if (showCalories(totalOrderCalories)) {
            return `${getCurrencyString(total.cost, currencyCode)} | ${getCaloriesString(
                totalOrderCalories.value
            )}`;
        }

        return getCurrencyString(total.cost, currencyCode);
    }, [currencyCode, getCaloriesString, total.cost, totalOrderCalories]);

    return (
        <StyledBox onClick={handleOrderClick} width="100%">
            <Grid container justifyContent="space-between">
                <Grid size={7}>
                    <Typography
                        color="textPrimary"
                        className={clsx(classes.textOpacity, classes.productNames)}
                    >
                        {productsString}
                    </Typography>
                </Grid>
                <Grid size={4}>
                    <Typography color="textPrimary" align="right" className={classes.textOpacity}>
                        {date}
                    </Typography>
                </Grid>
            </Grid>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>{priceString}</Typography>
                <IconButton
                    classes={{ root: classes.iconButton }}
                    onClick={handleAddToBasketClick}
                    disabled={!orderingEnabled}
                    size="large"
                >
                    <AddShoppingCartOutlined className={classes.icon} />
                </IconButton>
            </Box>
        </StyledBox>
    );
};
